import React, { useState } from 'react';
import { useCronContext } from './CronContext.js';

const statusMeta = {
    success: { label: 'Success', className: 'admin-badge-green' },
    failure: { label: 'Failed', className: 'admin-badge-red' },
    in_progress: { label: 'Running', className: 'admin-badge-yellow' },
    queued: { label: 'Queued', className: 'admin-badge-yellow' },
    cancelled: { label: 'Cancelled', className: 'admin-badge-gray' },
    skipped: { label: 'Skipped', className: 'admin-badge-gray' },
};

const formatDuration = (start, end) => {
    if (!start || !end) return '—';
    const ms = new Date(end) - new Date(start);
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
};

const AdminCron = () => {
    const { runs, loadingRuns, isRunning, fetchRuns, triggerCron, tokenError } = useCronContext();
    const [triggering, setTriggering] = useState(false);
    const [message, setMessage] = useState(null);

    const flash = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage(null), 6000);
    };

    const handleTrigger = async () => {
        setTriggering(true);
        const result = await triggerCron();
        flash(result.message, result.ok ? 'success' : 'error');
        setTriggering(false);
    };

    const triggerDisabled = triggering || isRunning;

    return (
        <div className="admin-section">
            <h2 className="admin-section-title">Cron Control</h2>

            {message && (
                <div className={`admin-message admin-message-${message.type}`}>{message.text}</div>
            )}

            {tokenError && (
                <div className="admin-message admin-message-error">{tokenError}</div>
            )}

            <div className="admin-cron-trigger">
                <div>
                    <strong>Schedule:</strong> Every 4 hours (at :15 past). Runs: nfts → stats → floor.
                    {isRunning && <span style={{ marginLeft: 10, color: '#ffd50d' }}>● Cron is currently running</span>}
                </div>
                <button
                    className="admin-btn admin-btn-primary"
                    onClick={handleTrigger}
                    disabled={triggerDisabled}
                    title={isRunning ? 'A cron is already running' : 'Trigger a manual cron run'}
                >
                    {triggering ? 'Triggering...' : isRunning ? '⏳ Already Running' : '▶ Run Cron Now'}
                </button>
                <button
                    className="admin-btn admin-btn-ghost"
                    onClick={fetchRuns}
                    disabled={loadingRuns}
                >
                    {loadingRuns ? 'Refreshing...' : '↻ Refresh'}
                </button>
            </div>

            {isRunning && (
                <div className="admin-message admin-message-success">
                    Auto-refreshing every 10s while the cron is running...
                </div>
            )}

            <div className="admin-cron-setup">
                <details>
                    <summary>GitHub token setup instructions</summary>
                    <p>The "Run Cron Now" button requires a GitHub Personal Access Token (PAT) stored in Firestore.</p>
                    <ol>
                        <li>Go to GitHub → Settings → Developer settings → Personal access tokens → Fine-grained tokens.</li>
                        <li>Create a token with <strong>Actions: Read and write</strong> permission on the <code>digital-crab/yellow-mythics</code> repository.</li>
                        <li>In Firebase Console → Firestore → <code>config</code> collection → create document <code>adminSecrets</code>.</li>
                        <li>Add a field: <code>githubToken</code> (string) = your PAT value.</li>
                    </ol>
                </details>
            </div>

            <h3 className="admin-sub-title">Recent Runs</h3>

            {loadingRuns ? (
                <div className="admin-loading">Loading runs...</div>
            ) : runs.length === 0 ? (
                <p className="admin-muted">No workflow runs found.</p>
            ) : (
                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Status</th>
                                <th>Trigger</th>
                                <th>Started</th>
                                <th>Duration</th>
                                <th>Link</th>
                            </tr>
                        </thead>
                        <tbody>
                            {runs.map(run => {
                                const conclusionOrStatus = run.conclusion || run.status;
                                const meta = statusMeta[conclusionOrStatus] || { label: conclusionOrStatus, className: 'admin-badge-gray' };
                                return (
                                    <tr key={run.id}>
                                        <td>
                                            <span className={`admin-badge ${meta.className}`}>{meta.label}</span>
                                        </td>
                                        <td>{run.event}</td>
                                        <td>{formatDate(run.run_started_at || run.created_at)}</td>
                                        <td>{formatDuration(run.run_started_at || run.created_at, run.updated_at)}</td>
                                        <td>
                                            <a
                                                href={run.html_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="admin-link"
                                            >
                                                View logs ↗
                                            </a>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdminCron;
